import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMyShiftSwaps, getPendingShiftSwaps } from '../api/Attendance/shiftSwap';
import { ShiftSwapRequestView } from '../models/shiftSwap';

interface UseShiftSwapsResult {
  mySwaps: ShiftSwapRequestView[];
  pendingApprovals: ShiftSwapRequestView[];
  loading: boolean;
  error: string | null;
  // Full refresh: toggles `loading` (shows the skeleton). Use after an explicit user action.
  refresh: () => Promise<void>;
  // Silent background revalidation: refetches without toggling `loading`, so focus/interval
  // polling never flashes the skeleton over content the user is already reading.
  revalidate: () => Promise<void>;
}

/**
 * Loads the current user's shift swaps and, for approvers, the swaps awaiting
 * their decision. `canApprove` gates the second request so non-managers never
 * hit the approvals endpoint.
 */
export function useShiftSwaps(canApprove: boolean): UseShiftSwapsResult {
  const { t } = useTranslation();
  const [mySwaps, setMySwaps] = useState<ShiftSwapRequestView[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<ShiftSwapRequestView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Shared fetch body. `silent` skips the loading toggle so background revalidation
  // (focus / interval polling) swaps in fresh data without flashing the skeleton.
  const load = useCallback(async (silent: boolean) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const requests: Promise<unknown>[] = [getMyShiftSwaps()];
      if (canApprove) requests.push(getPendingShiftSwaps());
      const [mineRes, pendingRes] = (await Promise.all(requests)) as [
        Awaited<ReturnType<typeof getMyShiftSwaps>>,
        Awaited<ReturnType<typeof getPendingShiftSwaps>> | undefined
      ];

      if (mineRes.errors.length === 0) {
        setMySwaps(mineRes.payload);
      }
      if (pendingRes && pendingRes.errors.length === 0) {
        setPendingApprovals(pendingRes.payload);
      } else if (!canApprove) {
        setPendingApprovals([]);
      }
    } catch {
      // A failed silent poll must not blow away content the user is reading; only the
      // foreground load surfaces the error state.
      if (!silent) setError(t('Could not load shift swaps'));
    }
    if (!silent) setLoading(false);
  }, [canApprove, t]);

  const refresh = useCallback(() => load(false), [load]);
  const revalidate = useCallback(() => load(true), [load]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { mySwaps, pendingApprovals, loading, error, refresh, revalidate };
}

export default useShiftSwaps;
